// Shared types between frontend and backend

export interface DataBody {
  id: string;
  userId: string;
  orgId: string | null;
  name: string;
  webhookToken: string;
  status: 'active' | 'paused' | 'archived';
  dataRetentionDays: number;
  rateLimitPerSecond: number;
  concurrencyLimit: number;
  /** Who sends leads here (Source.id). Null for bodies created before sources existed. */
  sourceId: string | null;
  /**
   * Source name, denormalised by the LIST endpoint so the dashboard's Source
   * column needs no extra fetch. Undefined on single-body responses; null on
   * bodies that predate sources.
   */
  sourceName?: string | null;
  createdAt: string;
  updatedAt: string;
  brainFields?: BrainField[];
  vertebrae?: Vertebra[];
  legs?: Leg[];        // named leg containers (since migration 005)
  legNodes?: LegNode[];
  stats?: Record<string, number>;
}

/**
 * Dashboard volume + delivery rate for one data body, from GET
 * /api/data-bodies/stats. Every count is leads the BRAIN ACCEPTED — i.e.
 * leads that will roll down the spine — not raw rows received at the door.
 */
export interface BodyVolume {
  /** Accepted since org-local midnight. */
  today: number;
  /** Accepted over the last 7 org-local days, today included. */
  sevenDay: number;
  /** Accepted, all time. */
  gross: number;
  /** Leg-node deliveries since org-local midnight. */
  deliveriesToday: number;
  /**
   * deliveriesToday / today as a RATIO, not a percent: 1.98 renders as 198%.
   * Routinely exceeds 1 because one lead fans out to several legs — never
   * clamp it. null when nothing came in today, which renders as an em dash.
   */
  deliveryRate: number | null;
}

/**
 * A Source is the company/person/entity that sends leads INTO a data body —
 * the publisher, vendor, affiliate or client POSTing to its webhook URL.
 * Account-level, so one source can own several data bodies.
 *
 * Not to be confused with the reporting "Type" split (CSV / Webhook / API),
 * which is how a lead arrived rather than who sent it.
 */
export interface Source {
  id: string;
  accountId: string;
  name: string;
  contactName: string | null;
  contactEmail: string | null;
  contactPhone: string | null;
  addressLine1: string | null;
  addressLine2: string | null;
  city: string | null;
  state: string | null;
  postalCode: string | null;
  country: string | null;
  notes: string | null;
  status: 'active' | 'archived';
  createdAt: string;
  updatedAt: string;
  /** Data bodies currently assigned to this source (from the list/detail API). */
  bodyCount?: number;
}

/** Writable fields on a Source. Everything but `name` is optional. */
export interface SourceInput {
  name: string;
  contactName?: string | null;
  contactEmail?: string | null;
  contactPhone?: string | null;
  addressLine1?: string | null;
  addressLine2?: string | null;
  city?: string | null;
  state?: string | null;
  postalCode?: string | null;
  country?: string | null;
  notes?: string | null;
}

/** A source plus the data bodies assigned to it (GET /api/sources/:id). */
export interface SourceDetail extends Source {
  dataBodies: Array<{ id: string; name: string; status: string; createdAt: string }>;
}

/**
 * A Client is the person/company/destination that RECEIVES leads out of a
 * delivery leg-node — the buyer we POST to, drop a file for, or append a row
 * for. The outbound mirror of Source.
 *
 *   Source = who sends leads IN  (attribute of a data body)
 *   Client = who receives leads OUT (attribute of a delivery leg-node)
 *
 * Names are capped at 25 characters so they fit on a leg-node card.
 */
export interface Client {
  id: string;
  accountId: string;
  name: string;
  contactName: string | null;
  contactEmail: string | null;
  contactPhone: string | null;
  addressLine1: string | null;
  addressLine2: string | null;
  city: string | null;
  state: string | null;
  postalCode: string | null;
  country: string | null;
  notes: string | null;
  status: 'active' | 'archived';
  createdAt: string;
  updatedAt: string;
  /** Destinations currently wired to this client. */
  nodeCount?: number;
  /** Every destination ever wired to it, deleted ones included. */
  totalNodeCount?: number;
  /** Destinations that have actually delivered (= locked assignments). */
  deliveringNodeCount?: number;
}

/** Writable fields on a Client. Everything but `name` is optional. */
export interface ClientInput {
  name: string;
  contactName?: string | null;
  contactEmail?: string | null;
  contactPhone?: string | null;
  addressLine1?: string | null;
  addressLine2?: string | null;
  city?: string | null;
  state?: string | null;
  postalCode?: string | null;
  country?: string | null;
  notes?: string | null;
}

/** Hard cap on a client name. Mirrored by the API and by a CHECK in 031. */
export const CLIENT_NAME_MAX = 25;

/**
 * One row of the durable client-to-leg-node ledger.
 *
 * `locked` is the rule that matters in the UI: once a node has delivered even
 * one lead, its client can never be changed, so the Client field renders as a
 * frozen chip instead of a combobox. `detachedAt` marks a node whose leg or
 * data body has been deleted — the row survives so the client keeps the
 * statistics.
 */
export interface ClientNodeAssignment {
  legNodeId: string;
  clientId: string;
  dataBodyId: string | null;
  dataBodyName: string | null;
  legId: string | null;
  legName: string | null;
  nodeName: string | null;
  nodeType: string | null;
  assignedAt: string;
  firstDeliveredAt: string | null;
  detachedAt: string | null;
  locked: boolean;
}

/** A client plus every node it has ever been assigned to (GET /api/clients/:id). */
export interface ClientDetail extends Client {
  assignments: ClientNodeAssignment[];
}

export interface Leg {
  id: string;
  dataBodyId: string;
  name: string;
  position: number;
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
}

export interface BrainField {
  id: string;
  dataBodyId: string;
  fieldName: string;
  fieldLabel: string;
  fieldType: 'text' | 'email' | 'phone' | 'address' | 'number' | 'boolean' | 'custom';
  isRequired: boolean;
  source: 'discovered' | 'manual';
  position: number;
}

export type VertebraeType =
  | 'email_validation' | 'email_oversight_validation' | 'free_email_check'
  | 'phone_validation' | 'free_phone_check' | 'address_validation'
  | 'data_enrichment' | 'database_lookup' | 'custom_api'
  | 'filter_rule' | 'dedup_check' | 'format_transform'
  | 'email_suppression' | 'phone_suppression'
  | 'bigsky_enrichment' | 'owned_enrichment';

export interface Vertebra {
  id: string;
  dataBodyId: string;
  type: VertebraeType;
  name: string;
  position: number;
  isActive: boolean;
  config: Record<string, any>;
  onFail: 'stop' | 'skip' | 'flag';
  createdAt: string;
  updatedAt: string;
}

export interface VertebraTemplate {
  type: VertebraeType;
  name: string;
  description: string;
  icon: string;
  category: string;
  requiresApiSetup: boolean;
}

export type LegNodeType = 'webhook_output' | 'custom_api' | 'time_delay' | 'condition' | 'split' | 'csv_download' | 'cloud_data_destination' | 'google_sheets' | 'sftp_ftp';

// Per-field strictness inside a csv_download leg-node config.
export interface CsvFieldChoice {
  name: string;
  required: boolean;
}

// Filter rule shape — identical to vertebrae filter_rule; reused here so the
// CSV download UI can mount the same rule-builder component.
export interface CsvFilterRule {
  field: string;
  operator: 'equals' | 'not_equals' | 'contains' | 'not_contains' | 'greater_than' | 'less_than' | 'exists' | 'not_exists' | 'regex';
  value: string;
  caseSensitive?: boolean;
}

// One row from csv_download_files (history list inside an instance).
export interface CsvDownloadFile {
  id: string;
  legNodeId: string;
  dataBodyId: string;
  filename: string;
  rowCount: number;
  fileSizeBytes: number | null;
  status: 'queued' | 'processing' | 'completed' | 'failed' | 'expired';
  progressPct: number;
  error: string | null;
  createdAt: string;
  startedAt: string | null;
  completedAt: string | null;
  expiresAt: string | null;
  downloadUrl?: string | null;        // signed R2 URL — only fetched on demand
}

// Server response from /csv-download/:id/summary
export interface CsvDownloadSummary {
  totalInDateRange: number;
  passingFilters: number;
  missingRequiredField: number;
  alreadyDownloaded: number;          // 0 when unique=false
  eligible: number;                   // final count that would actually be exported
  cappedAt: number | null;            // non-null if eligible was clipped by the hard cap
  byFilter: Array<{ rule: CsvFilterRule; rejected: number }>;
  computedAt: string;                 // server timestamp, mostly for cache debugging
}

export interface LegNode {
  id: string;
  dataBodyId: string;
  legId: string;                // which leg this node belongs to
  parentNodeId: string | null;
  type: LegNodeType;
  name: string;
  position: number;
  isActive: boolean;
  config: Record<string, any>;
  /** Who receives leads from this node. Null for non-delivery types and for
   *  nodes created before clients existed. */
  clientId?: string | null;
  /** Denormalised for display on the leg-node card — set by the builder when
   *  it resolves clients, not returned by the node endpoints themselves. */
  clientName?: string | null;
}

/**
 * Leg-node types that actually deliver leads to someone, and therefore
 * require a client. Flow-control nodes (time_delay / condition / split) route
 * leads inside a leg and deliver to nobody. Mirrors DELIVERY_NODE_TYPES in
 * the backend's models/Client.ts — keep the two in step.
 */
export const DELIVERY_NODE_TYPES: LegNodeType[] = [
  'webhook_output',
  'custom_api',
  'csv_download',
  'cloud_data_destination',
  'google_sheets',
  'sftp_ftp',
];

export function isDeliveryNodeType(type: string | null | undefined): boolean {
  return DELIVERY_NODE_TYPES.includes(type as LegNodeType);
}

export interface LegNodeTemplate {
  type: LegNodeType;
  name: string;
  description: string;
  icon: string;
}

export interface Lead {
  id: string;
  dataBodyId: string;
  data: Record<string, any>;
  enrichedData: Record<string, any>;
  status: string;
  failReason: string | null;
  legsAttempted: number;       // # of active, non-empty legs the lead was sent to
  legsSucceeded: number;       // # of those legs whose tree returned all-pass
  processingLog: ProcessingLogEntry[];
  receivedAt: string;
  completedAt: string | null;
  source?: string | null;      // 'webhook'/'api' (URL push) | 'csv' (import) | null (legacy). Drives the "API Log" pill.
  // Outbound API-Log indicator, attached by the per-leg leads endpoint. null
  // when this leg produced no dispatch (e.g. non-Custom-API legs). Drives the
  // outbound "API Log" pill in the leg's Pass/Failed leads table.
  dispatchLog?: { statusCode: number | null; category: string } | null;
}

export interface ProcessingLogEntry {
  vertebraId?: string;
  legNodeId?: string;
  stepName: string;
  status: 'pass' | 'fail' | 'skip' | 'error';
  message?: string;
  duration: number;
  timestamp: string;
}
